import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import { useSelector, useDispatch } from 'src/redux/store';
import Scrollbar from 'src/components/Scrollbar';
import Iconify from 'src/components/Iconify';
import SkeletonListaChats from 'src/components/skeleton/chats/SkeletonListaChats';
import { getSocialPosts, getSocialComments, setSelectedPost, syncSocialCommentsThunk, clearUnreadComments } from 'src/redux/slices/socialComments';
import { Button } from 'src/@/components/ui/button';

import CommentPostItem from './CommentPostItem';

export default function CommentsPostList() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { t } = useTranslation('chat');
  const { enqueueSnackbar } = useSnackbar();

  const { posts, loadingPosts, selectedPost, syncing } = useSelector((state) => state.socialComments);

  useEffect(() => {
    if (user?.clienteId) dispatch(getSocialPosts(user.clienteId));
    // A lista na tela ja mostra o que chegou — o badge da aba nao faz mais sentido.
    dispatch(clearUnreadComments());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.clienteId]);

  const handleSelectPost = (post) => {
    dispatch(setSelectedPost(post));
    dispatch(getSocialComments(user.clienteId, post.platform, post.post_id));
  };

  const handleSync = async () => {
    enqueueSnackbar(t('comments.syncing'), { variant: 'info' });
    const resultado = await dispatch(syncSocialCommentsThunk({ cliente_id: user.clienteId }));
    if (!resultado?.success) enqueueSnackbar(t('comments.actionError'), { variant: 'error' });
  };

  return (
    <div className="flex flex-col w-full h-full">
      {/* Mini-header da aba de comentarios (a busca/filtros de chats nao se aplicam aqui) */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-50 dark:bg-violet-500/10">
            <Iconify icon="eva:message-circle-outline" width={18} className="text-violet-600" />
          </div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('sidebar.mainTabs.comments')}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="h-7 px-2 text-xs text-gray-500 dark:text-neutral-400 hover:text-violet-600"
        >
          <Iconify
            icon={syncing ? 'svg-spinners:ring-resize' : 'solar:refresh-linear'}
            width={14}
            className="mr-1"
          />
          {t('comments.sync')}
        </Button>
      </div>

      {loadingPosts ? (
        <SkeletonListaChats />
      ) : (
        <Scrollbar sx={{ height: 'calc(100vh - 200px)' }}>
          {posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-violet-50 dark:bg-violet-500/10">
                <Iconify icon="eva:message-circle-outline" width={28} className="text-violet-600" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-neutral-300">{t('comments.emptyPosts')}</p>
              <p className="text-xs text-gray-400 dark:text-neutral-500 mt-1">{t('comments.emptyPostsHint')}</p>
            </div>
          ) : (
            posts.map((post) => (
              <CommentPostItem
                key={post.post_id}
                post={post}
                isActive={selectedPost?.post_id === post.post_id}
                onSelect={handleSelectPost}
              />
            ))
          )}
        </Scrollbar>
      )}
    </div>
  );
}
